import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  SxProps,
  Tab,
  Tabs,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import { sliderClasses } from '@mui/material/Slider';
import { api } from '../api/apiService';
import { AnalyticProfileContext, AnalyticProfileContextType } from '../contexts/AnalyticProfileContext';
import type { PublishVenue } from '../types/publish-venue';
import { normalizePublishVenuesListResponse } from '../utils/publishVenuesResponse';
import { FastTrackingDashboardTab } from '../components/fast-tracking/FastTrackingDashboardTab';

type FastDomain = {
  id: string;
  host_pattern: string;
  trusted: boolean;
  enabled: boolean;
  source?: string;
};

type FastRule = {
  id: string;
  domain_id: string;
  action: string;
  publish_venues?: string[];
  draft_criterion_id?: string;
  generate_article_image?: boolean;
  generate_article_quiz?: boolean;
  enabled: boolean;
};

/** Single allowed output type for now; API may extend later. */
const DRAFT_OUTPUT_KIND_ARTICLE = 'article' as const;

const WORD_LENGTH_PRESETS = [
  { key: 'flash' as const, label: 'Flash', min: 150, max: 300 },
  { key: 'short_form' as const, label: 'Short form', min: 400, max: 600 },
  { key: 'standard' as const, label: 'Standard', min: 800, max: 1300 },
  { key: 'long_form' as const, label: 'Long-form', min: 1500, max: 2500 },
] as const;

type WordPresetKey = (typeof WORD_LENGTH_PRESETS)[number]['key'];

type FastDraftCriterion = {
  id: string;
  name?: string;
  target_length_chars: number;
  target_length_words?: number;
  target_word_min?: number;
  target_word_max?: number;
  length_preset?: WordPresetKey;
  output_kind: typeof DRAFT_OUTPUT_KIND_ARTICLE;
  enabled: boolean;
};

function formatDraftCriterionLabel(c: FastDraftCriterion): string {
  const w =
    typeof c.target_length_words === 'number' && Number.isFinite(c.target_length_words)
      ? c.target_length_words
      : Math.max(1, Math.round((c.target_length_chars || 0) / 6));
  const name = (c.name ?? '').trim() || 'Untitled';
  return `${name} (${w} words)`;
}

const WORD_SLIDER_MIN = 150;
const WORD_SLIDER_MAX = 2500;
const WORD_MILESTONES = [150, 300, 400, 600, 800, 1300, 1500, 2500] as const;

/** Slider may only rest on these word counts. */
function snapWordToMilestone(words: number): number {
  const w = Number.isFinite(words) ? Math.round(words) : 800;
  let best: (typeof WORD_MILESTONES)[number] = WORD_MILESTONES[0];
  let bestDist = Infinity;
  for (const m of WORD_MILESTONES) {
    const d = Math.abs(w - m);
    if (d < bestDist) {
      bestDist = d;
      best = m;
    }
  }
  return best;
}

/** Same as {@link snapWordToMilestone}; kept so older call sites or merges still typecheck. */
const clampWordSlider = snapWordToMilestone;

function presetIndexForWordValue(words: number): number {
  const w = snapWordToMilestone(words);
  for (let i = 0; i < WORD_LENGTH_PRESETS.length; i++) {
    const { min, max } = WORD_LENGTH_PRESETS[i];
    if (w >= min && w <= max) return i;
  }
  if (w < WORD_LENGTH_PRESETS[0].min) return 0;
  if (w > WORD_LENGTH_PRESETS[WORD_LENGTH_PRESETS.length - 1].max) return WORD_LENGTH_PRESETS.length - 1;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < WORD_LENGTH_PRESETS.length; i++) {
    const { min, max } = WORD_LENGTH_PRESETS[i];
    const dist = w < min ? min - w : w > max ? w - max : 0;
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

function wordsFromCriterion(c: FastDraftCriterion): number {
  let w: number;
  if (typeof c.target_length_words === 'number' && Number.isFinite(c.target_length_words)) {
    w = Math.round(c.target_length_words);
  } else {
    w = Math.max(1, Math.round(c.target_length_chars / 6));
  }
  return snapWordToMilestone(w);
}

function criterionSavePayload(name: string, words: number, enabled: boolean) {
  const w = snapWordToMilestone(words);
  const presetIx = presetIndexForWordValue(w);
  const p = WORD_LENGTH_PRESETS[presetIx];
  return {
    name: name.trim(),
    target_length_words: w,
    target_word_min: p.min,
    target_word_max: p.max,
    length_preset: p.key,
    target_length_chars: Math.round(w * 6),
    output_kind: DRAFT_OUTPUT_KIND_ARTICLE,
    enabled,
  };
}

/** Backend stores one cumulative action; UI maps to three ordered stages. */
const ACTION_DRAFT = 'draft_human_gate';
const ACTION_READY = 'draft_ready_publish';
const ACTION_PUBLISH = 'draft_publish_cms';

type FastTrackStages = {
  draft: boolean;
  ready: boolean;
  publish: boolean;
};

function stagesFromAction(action: string): FastTrackStages {
  if (action === ACTION_PUBLISH) return { draft: true, ready: true, publish: true };
  if (action === ACTION_READY) return { draft: true, ready: true, publish: false };
  return { draft: true, ready: false, publish: false };
}

/** At least draft is required for a meaningful rule; all-off coerces to draft-only on save. */
function actionFromStages(s: FastTrackStages): string {
  if (s.publish) return ACTION_PUBLISH;
  if (s.ready) return ACTION_READY;
  return ACTION_DRAFT;
}

function toggleStageReady(checked: boolean, s: FastTrackStages): FastTrackStages {
  if (checked) return { draft: true, ready: true, publish: s.publish };
  return { draft: s.draft, ready: false, publish: false };
}

function toggleStagePublish(checked: boolean, s: FastTrackStages): FastTrackStages {
  if (checked) return { draft: true, ready: true, publish: true };
  return { draft: s.draft, ready: s.ready, publish: false };
}

function dedupeVenues(v: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of v) {
    const s = String(raw).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

const venueFieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fafafa' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
};

/** Value entry for a slug missing from the tenant list (legacy rule data). */
function publishVenuePlaceholderForUnknownSlug(slug: string): PublishVenue {
  return {
    id: `__unknown_slug__:${slug}`,
    slug,
    name: slug,
    enabled: false,
    created_at: 0,
    updated_at: 0,
    cms_key_configured: false,
  };
}

function publishVenueOptionForSlug(slug: string, venues: PublishVenue[]): PublishVenue {
  const found = venues.find((v) => v.slug === slug);
  return found ?? publishVenuePlaceholderForUnknownSlug(slug);
}

function publishVenueChipLabel(slug: string, venues: PublishVenue[]): string {
  const v = venues.find((x) => x.slug === slug);
  if (v) return `${v.name} (${v.slug})`;
  return slug;
}

type VenueAutocompleteInputProps = {
  venues: PublishVenue[];
  value: string[];
  onChange: (venues: string[]) => void;
  disabled?: boolean;
  label?: string;
  sx?: SxProps<Theme>;
};

type DraftCriterionAutocompleteProps = {
  criteria: FastDraftCriterion[];
  value: string;
  onChange: (criterionId: string) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

const DraftCriterionAutocomplete: React.FC<DraftCriterionAutocompleteProps> = ({
  criteria,
  value,
  onChange,
  disabled,
  sx,
}) => {
  const selected = criteria.find((c) => c.id === value) ?? null;
  return (
    <Autocomplete
      disabled={disabled || criteria.length === 0}
      options={criteria}
      getOptionLabel={(c) => formatDraftCriterionLabel(c)}
      value={selected}
      onChange={(_, c) => onChange(c?.id ?? '')}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Draft criteria"
          placeholder={criteria.length === 0 ? 'No criteria yet' : 'Search…'}
          size="small"
          sx={{ ...venueFieldSx }}
        />
      )}
    />
  );
};

const VenueAutocompleteInput: React.FC<VenueAutocompleteInputProps> = ({
  venues,
  value,
  onChange,
  disabled,
  label = 'Publish venues',
  sx,
}) => {
  const [input, setInput] = useState('');
  const selectedOptions = value.map((slug) => publishVenueOptionForSlug(slug, venues));
  return (
    <Autocomplete
      multiple
      freeSolo={false}
      disabled={disabled}
      options={venues}
      getOptionLabel={(v) =>
        String(v.id).startsWith('__unknown_slug__:')
          ? `${v.slug} (not in publish venues)`
          : v.name?.trim()
            ? `${v.name.trim()} (${v.slug})`
            : v.slug
      }
      isOptionEqualToValue={(a, b) => a.slug === b.slug}
      value={selectedOptions}
      onChange={(_, newValue) => onChange(dedupeVenues((newValue as PublishVenue[]).map((v) => v.slug)))}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        if (reason === 'reset') return;
        setInput(v);
      }}
      filterSelectedOptions
      renderTags={() => null}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={
            venues.length === 0 ? 'Add venues under Admin → Publish venues' : 'Search publish venues…'
          }
          size="small"
          sx={{ ...venueFieldSx }}
        />
      )}
    />
  );
};

type VenueChipsRowProps = {
  venues: PublishVenue[];
  value: string[];
  onChange: (venues: string[]) => void;
  disabled?: boolean;
};

const VenueChipsRow: React.FC<VenueChipsRowProps> = ({ venues, value, onChange, disabled }) => (
  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ width: '100%' }}>
    {value.map((slug) => (
      <Chip
        key={slug}
        label={publishVenueChipLabel(slug, venues)}
        size="small"
        variant="outlined"
        disabled={disabled}
        onDelete={disabled ? undefined : () => onChange(value.filter((x) => x !== slug))}
        sx={{ borderColor: '#52525b', color: '#e4e4e7' }}
      />
    ))}
  </Stack>
);

const checkboxSx = { color: '#71717a', '&.Mui-checked': { color: '#818cf8' }, p: 0.25 };

const STAGE_TOOLTIPS = {
  draft: 'Draft',
  ready: 'Ready to publish',
  publish: 'Publish',
} as const;

/** Width matches three tight checkbox columns */
const STAGES_GRID_WIDTH = 108;

const stageHeaderIconSx = { color: '#a1a1aa', fontSize: 22, display: 'block' };

const RuleStagesColumnHeader: React.FC = () => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{ width: STAGES_GRID_WIDTH, mx: 'auto' }}
  >
    <Tooltip title={STAGE_TOOLTIPS.draft}>
      <EditNoteOutlinedIcon sx={stageHeaderIconSx} aria-label="Draft stage" />
    </Tooltip>
    <Tooltip title={STAGE_TOOLTIPS.ready}>
      <CheckCircleOutlineIcon sx={stageHeaderIconSx} aria-label="Ready to publish stage" />
    </Tooltip>
    <Tooltip title={STAGE_TOOLTIPS.publish}>
      <PublishOutlinedIcon sx={stageHeaderIconSx} aria-label="Publish stage" />
    </Tooltip>
  </Stack>
);

type RuleStagesCheckboxesProps = {
  value: FastTrackStages;
  onChange: (next: FastTrackStages) => void;
  disabled?: boolean;
  /** When true, the Draft stage stays on and cannot be cleared (fast-track always drafts). */
  draftStageLocked?: boolean;
};

const RuleStagesCheckboxes: React.FC<RuleStagesCheckboxesProps> = ({
  value,
  onChange,
  disabled,
  draftStageLocked = true,
}) => {
  const base = { ...value, draft: true };
  const emit = (next: FastTrackStages) => onChange({ ...next, draft: true });
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: STAGES_GRID_WIDTH, mx: 'auto' }}
    >
      <Tooltip title={STAGE_TOOLTIPS.draft}>
        <span>
          <Checkbox
            size="small"
            checked
            disabled={disabled || draftStageLocked}
            sx={checkboxSx}
            inputProps={{ 'aria-label': 'Draft stage (always on)' }}
          />
        </span>
      </Tooltip>
      <Tooltip title={STAGE_TOOLTIPS.ready}>
        <span>
          <Checkbox
            size="small"
            checked={base.ready}
            disabled={disabled}
            onChange={(_, c) => emit(toggleStageReady(c, base))}
            sx={checkboxSx}
            inputProps={{ 'aria-label': 'Ready to publish stage' }}
          />
        </span>
      </Tooltip>
      <Tooltip title={STAGE_TOOLTIPS.publish}>
        <span>
          <Checkbox
            size="small"
            checked={base.publish}
            disabled={disabled}
            onChange={(_, c) => emit(toggleStagePublish(c, base))}
            sx={checkboxSx}
            inputProps={{ 'aria-label': 'Publish stage' }}
          />
        </span>
      </Tooltip>
    </Stack>
  );
};

/** Shared column template for rules table (domain | stages | draft criteria | venues | enabled | actions) */
const rulesGridSx = {
  display: 'grid',
  gap: { xs: 1, sm: 1.25 },
  alignItems: 'center',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'minmax(72px, 0.42fr) minmax(108px, 108px) minmax(140px, 0.95fr) minmax(100px, 0.62fr) minmax(92px, auto) minmax(112px, auto)',
  },
} as const;

const rulesHeaderTypographySx = {
  color: '#e4e4e7',
  fontWeight: 600,
  fontSize: '1rem',
  lineHeight: 1.2,
  letterSpacing: '0.01em',
};

/** Nudges the venues column away from stage checkboxes; pairs with wider enabled/actions columns. */
const publishVenuesColumnInsetSx = {
  pl: { sm: 2.5 },
  minWidth: 0,
} as const;

const rulesVenuesColumnSx = {
  ...publishVenuesColumnInsetSx,
  width: '100%',
  maxWidth: { sm: 280 },
  minWidth: 0,
} as const;

const criteriaGridSx = {
  display: 'grid',
  gap: { xs: 1, sm: 1.25 },
  alignItems: 'center',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'minmax(120px, 1fr) minmax(220px, 2.2fr) minmax(88px, 0.5fr) minmax(92px, auto) minmax(96px, auto)',
  },
} as const;

/** Even index: label below tick; odd index: label above tick (MUI sets `data-index` on each mark label). */
const wordCountSliderMarks = WORD_MILESTONES.map((value) => ({
  value,
  label: String(value),
}));

/** Odd milestones sit above the rail; even below — both anchored to rail center (`top: 50%`) so labels never sit on the track. */
const wordCountSliderMarkLabelSx: SxProps<Theme> = Object.fromEntries(
  WORD_MILESTONES.map((_, index) => [
    `& .MuiSlider-markLabel[data-index="${index}"]`,
    {
      fontVariantNumeric: 'tabular-nums',
      fontSize: 11,
      fontWeight: 500,
      color: '#a1a1aa',
      whiteSpace: 'nowrap',
      zIndex: 1,
      overflow: 'visible',
      lineHeight: 1.2,
      top: '50%',
      transformOrigin: 'center center',
      ...(index % 2 === 0
        ? { transform: 'translateX(-50%) translateY(10px)' }
        : { transform: 'translateX(-50%) translateY(calc(-100% - 10px))' }),
    },
  ]),
) as SxProps<Theme>;

type CriterionWordCountSliderProps = {
  disabled?: boolean;
  words: number;
  onWordsChange: (words: number) => void;
  onWordsCommitted?: (words: number) => void;
};

const outputArticleChipSx = {
  borderColor: '#52525b',
  color: '#e4e4e7',
  fontWeight: 600,
  bgcolor: '#27272a',
  height: 28,
} as const;

/** Output column: inset from slider, same line as name field. */
const draftCriterionOutputChipSx = {
  ...outputArticleChipSx,
  justifySelf: 'start',
  ml: { sm: 2 },
} as const;

const CriterionWordCountSlider: React.FC<CriterionWordCountSliderProps> = ({
  disabled,
  words,
  onWordsChange,
  onWordsCommitted,
}) => (
  <Box
    sx={{
      width: '100%',
      minWidth: 0,
      maxWidth: 520,
      display: 'flex',
      alignItems: 'center',
      overflow: 'visible',
      /* Nudge rail to the visual midline of labeled TextField + 28px chip in the grid row. */
      transform: 'translateY(4px)',
    }}
  >
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        pl: 0.5,
        pr: { xs: 0.5, sm: 1.5 },
        overflow: 'visible',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Slider
        value={snapWordToMilestone(words)}
        disabled={disabled}
        min={WORD_SLIDER_MIN}
        max={WORD_SLIDER_MAX}
        step={null}
        marks={wordCountSliderMarks}
        track={false}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${v} words`}
        onChange={(_, v) => onWordsChange(snapWordToMilestone(typeof v === 'number' ? v : v[0]))}
        onChangeCommitted={(_, v) => {
          const raw = snapWordToMilestone(typeof v === 'number' ? v : v[0]);
          onWordsCommitted?.(raw);
        }}
        sx={{
          width: '100%',
          py: 0,
          color: '#818cf8',
          overflow: 'visible',
          [`&.${sliderClasses.marked}`]: {
            marginTop: 5,
            marginBottom: 5,
          },
          '& .MuiSlider-rail': { opacity: 0.45, height: 6, borderRadius: 3 },
          '& .MuiSlider-track': { display: 'none' },
          '& .MuiSlider-thumb': {
            width: 20,
            height: 20,
            backgroundColor: '#bbf7d0',
            border: '2px solid #22c55e',
            boxShadow: '0 0 0 1px #166534',
            '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(34,197,94,0.28)' },
          },
          '& .MuiSlider-mark': {
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#fafafa',
            border: '2px solid #6366f1',
            boxSizing: 'border-box',
            opacity: 1,
            top: '50%',
          },
          '& .MuiSlider-markActive': {
            backgroundColor: '#bbf7d0',
            borderColor: '#22c55e',
            boxShadow: '0 0 0 1px #166534',
          },
          ...wordCountSliderMarkLabelSx,
          '& .MuiSlider-markLabelActive': {
            color: '#86efac',
            fontWeight: 700,
          },
        }}
      />
    </Box>
  </Box>
);

type DraftCriterionRowProps = {
  c: FastDraftCriterion;
  busy: boolean;
  basePath: string;
  run: (fn: () => Promise<void>) => Promise<void>;
};

const DraftCriterionRow: React.FC<DraftCriterionRowProps> = ({ c, busy, basePath, run }) => {
  const [name, setName] = useState(() => (c.name ?? '').trim());
  const [words, setWords] = useState(() => wordsFromCriterion(c));

  useEffect(() => {
    setName((c.name ?? '').trim());
    setWords(wordsFromCriterion(c));
  }, [c]);

  const persist = (nextName: string, nextWords: number, nextEnabled: boolean) =>
    run(async () => {
      await api.put(`${basePath}/draft-criteria/${c.id}`, criterionSavePayload(nextName, nextWords, nextEnabled));
    });

  return (
    <Box
      sx={{
        ...criteriaGridSx,
        px: 1,
        py: 0.65,
        border: '1px solid #27272a',
        borderRadius: 1,
        bgcolor: '#18181b',
      }}
    >
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          const t = name.trim();
          if (t === (c.name ?? '').trim()) return;
          void persist(t, words, c.enabled);
        }}
        required
        size="small"
        disabled={busy}
        sx={{ width: '100%', minWidth: 0, input: { color: '#fafafa' }, label: { color: '#a1a1aa' } }}
      />
      <CriterionWordCountSlider
        disabled={busy}
        words={words}
        onWordsChange={(w) => setWords(w)}
        onWordsCommitted={(w) => {
          const nw = snapWordToMilestone(w);
          setWords(nw);
          void persist(name.trim(), nw, c.enabled);
        }}
      />
      <Chip label="Article" size="small" variant="outlined" sx={draftCriterionOutputChipSx} />
      <Switch
        checked={c.enabled}
        onChange={(_, on) => void persist(name.trim(), words, on)}
        disabled={busy}
        size="small"
        inputProps={{ 'aria-label': 'Enabled' }}
        sx={{ justifySelf: 'center' }}
      />
      <Tooltip title="Delete criterion">
        <span>
          <IconButton
            size="small"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await api.delete(`${basePath}/draft-criteria/${c.id}`);
              })
            }
            sx={{ color: '#f87171', justifySelf: 'center' }}
            aria-label="Delete draft criterion"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const LayoutFastTracking: React.FC = () => {
  const { selectedProfileId } = useContext<AnalyticProfileContextType>(AnalyticProfileContext);
  const [tab, setTab] = useState(0);
  const [domains, setDomains] = useState<FastDomain[] | null>(null);
  const [rules, setRules] = useState<FastRule[] | null>(null);
  const [draftCriteria, setDraftCriteria] = useState<FastDraftCriterion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newHost, setNewHost] = useState('');
  const [newTrusted, setNewTrusted] = useState(false);
  const [newRuleDomainId, setNewRuleDomainId] = useState('');
  const [newRuleCriterionId, setNewRuleCriterionId] = useState('');
  const [newRuleStages, setNewRuleStages] = useState<FastTrackStages>(stagesFromAction(ACTION_DRAFT));
  const [newRuleVenues, setNewRuleVenues] = useState<string[]>([]);
  const [newRuleEnabled, setNewRuleEnabled] = useState(true);
  const [newRuleGenImage, setNewRuleGenImage] = useState(false);
  const [newRuleGenQuiz, setNewRuleGenQuiz] = useState(false);
  const [publishVenues, setPublishVenues] = useState<PublishVenue[]>([]);
  /** Existing rule row: publish-venues Autocomplete only while editing; chips are read-only otherwise. */
  const [publishVenuesEditingRuleId, setPublishVenuesEditingRuleId] = useState<string | null>(null);
  const [newCriterionName, setNewCriterionName] = useState('');
  const [newCriterionWords, setNewCriterionWords] = useState(800);
  const venueSaveTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const stagesSaveTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const criterionSaveTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const enrichmentSaveTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const configSyncForProfile = React.useRef<string | null>(null);

  const basePath = selectedProfileId
    ? `/api/analytic-profiles/${encodeURIComponent(selectedProfileId)}/fast-track`
    : null;

  const reload = useCallback(async () => {
    if (!basePath) return;
    setError(null);

    /** Axios 404s are turned into NotFoundError with `status`, not `response.status` (see axiosInstance). */
    const httpStatus = (e: unknown): number | undefined => {
      const err = e as { response?: { status?: number }; status?: number };
      return err.response?.status ?? err.status;
    };

    const fetchDomains = async (): Promise<FastDomain[]> => {
      try {
        const dRes = (await api.get(`${basePath}/domains`)) as { domains?: FastDomain[] };
        return Array.isArray(dRes?.domains) ? dRes.domains : [];
      } catch (e: unknown) {
        if (httpStatus(e) === 404) return [];
        throw e;
      }
    };

    const fetchRules = async (): Promise<FastRule[]> => {
      try {
        const rRes = (await api.get(`${basePath}/rules`)) as { rules?: FastRule[] };
        return Array.isArray(rRes?.rules) ? rRes.rules : [];
      } catch (e: unknown) {
        if (httpStatus(e) === 404) return [];
        throw e;
      }
    };

    type CriteriaResponse = { draft_criteria?: FastDraftCriterion[]; criteria?: FastDraftCriterion[] };
    const fetchCriteria = async (): Promise<FastDraftCriterion[]> => {
      try {
        const cRes = (await api.get(`${basePath}/draft-criteria`)) as CriteriaResponse;
        const raw = cRes?.draft_criteria ?? cRes?.criteria;
        return Array.isArray(raw) ? raw : [];
      } catch (e: unknown) {
        if (httpStatus(e) === 404) return [];
        throw e;
      }
    };

    const fetchPublishVenues = async (): Promise<PublishVenue[]> => {
      try {
        const res = await api.get('/api/publish-venues');
        return normalizePublishVenuesListResponse(res);
      } catch {
        return [];
      }
    };

    const [domainsArr, rulesArr, criteriaArr, venuesArr] = await Promise.all([
      fetchDomains(),
      fetchRules(),
      fetchCriteria(),
      fetchPublishVenues(),
    ]);
    setDomains(domainsArr);
    setRules(rulesArr);
    setDraftCriteria(criteriaArr);
    setPublishVenues(venuesArr);
  }, [basePath]);

  useEffect(() => {
    return () => {
      Object.values(venueSaveTimers.current).forEach((t) => clearTimeout(t));
      venueSaveTimers.current = {};
      Object.values(stagesSaveTimers.current).forEach((t) => clearTimeout(t));
      stagesSaveTimers.current = {};
      Object.values(criterionSaveTimers.current).forEach((t) => clearTimeout(t));
      criterionSaveTimers.current = {};
      Object.values(enrichmentSaveTimers.current).forEach((t) => clearTimeout(t));
      enrichmentSaveTimers.current = {};
    };
  }, []);

  const queueEnrichmentSave = useCallback(
    (ruleId: string, generateArticleImage: boolean, generateArticleQuiz: boolean) => {
      if (!basePath) return;
      const prev = enrichmentSaveTimers.current[ruleId];
      if (prev) clearTimeout(prev);
      enrichmentSaveTimers.current[ruleId] = setTimeout(async () => {
        delete enrichmentSaveTimers.current[ruleId];
        setError(null);
        try {
          await api.put(`${basePath}/rules/${encodeURIComponent(ruleId)}`, {
            generate_article_image: generateArticleImage,
            generate_article_quiz: generateArticleQuiz,
          });
        } catch (e: unknown) {
          setError(e instanceof Error ? e.message : 'Failed to save enrichment flags.');
          await reload();
        }
      }, 450);
    },
    [basePath, reload]
  );

  useEffect(() => {
    if (!draftCriteria?.length || newRuleCriterionId) return;
    const first = draftCriteria.find((c) => c.enabled !== false) ?? draftCriteria[0];
    if (first) setNewRuleCriterionId(first.id);
  }, [draftCriteria, newRuleCriterionId]);

  useEffect(() => {
    if (!basePath || !selectedProfileId) {
      setDomains(null);
      setRules(null);
      setDraftCriteria(null);
      setPublishVenues([]);
      setPublishVenuesEditingRuleId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDomains(null);
      setRules(null);
      setDraftCriteria(null);
      try {
        if (configSyncForProfile.current !== selectedProfileId) {
          configSyncForProfile.current = selectedProfileId;
          try {
            await api.post(`${basePath}/domains/sync-from-config`);
          } catch {
            /* best-effort: server config may omit manual_source_domains */
          }
        }
        if (!cancelled) await reload();
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load.');
          setDomains([]);
          setRules([]);
          setDraftCriteria([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [basePath, selectedProfileId, reload]);

  useEffect(() => {
    if (!publishVenuesEditingRuleId || !rules) return;
    if (!rules.some((x) => x.id === publishVenuesEditingRuleId)) {
      setPublishVenuesEditingRuleId(null);
    }
  }, [rules, publishVenuesEditingRuleId]);

  const queueVenueSave = useCallback(
    (ruleId: string, venues: string[]) => {
      if (!basePath) return;
      const prev = venueSaveTimers.current[ruleId];
      if (prev) clearTimeout(prev);
      venueSaveTimers.current[ruleId] = setTimeout(async () => {
        delete venueSaveTimers.current[ruleId];
        const sPending = stagesSaveTimers.current[ruleId];
        if (sPending) clearTimeout(sPending);
        delete stagesSaveTimers.current[ruleId];
        const cPending = criterionSaveTimers.current[ruleId];
        if (cPending) clearTimeout(cPending);
        delete criterionSaveTimers.current[ruleId];
        setError(null);
        try {
          await api.put(`${basePath}/rules/${encodeURIComponent(ruleId)}`, { publish_venues: venues });
        } catch (e: unknown) {
          setError(e instanceof Error ? e.message : 'Failed to save venues.');
          await reload();
        }
      }, 450);
    },
    [basePath, reload]
  );

  const queueStagesSave = useCallback(
    (ruleId: string, stages: FastTrackStages, publishVenues: string[]) => {
      if (!basePath) return;
      const prev = stagesSaveTimers.current[ruleId];
      if (prev) clearTimeout(prev);
      stagesSaveTimers.current[ruleId] = setTimeout(async () => {
        delete stagesSaveTimers.current[ruleId];
        const vPending = venueSaveTimers.current[ruleId];
        if (vPending) clearTimeout(vPending);
        delete venueSaveTimers.current[ruleId];
        const cPending = criterionSaveTimers.current[ruleId];
        if (cPending) clearTimeout(cPending);
        delete criterionSaveTimers.current[ruleId];
        setError(null);
        let normalized = stages;
        if (!stages.draft && !stages.ready && !stages.publish) {
          normalized = stagesFromAction(ACTION_DRAFT);
        }
        const action = actionFromStages(normalized);
        const venues = normalized.publish ? publishVenues : [];
        try {
          await api.put(`${basePath}/rules/${encodeURIComponent(ruleId)}`, {
            action,
            publish_venues: venues,
          });
          setRules((prev) =>
            (prev ?? []).map((x) =>
              x.id === ruleId ? { ...x, action, publish_venues: venues } : x
            )
          );
        } catch (e: unknown) {
          setError(e instanceof Error ? e.message : 'Failed to save stages.');
          await reload();
        }
      }, 450);
    },
    [basePath, reload]
  );

  const queueCriterionSave = useCallback(
    (ruleId: string, criterionId: string) => {
      if (!basePath || !criterionId) return;
      const prev = criterionSaveTimers.current[ruleId];
      if (prev) clearTimeout(prev);
      criterionSaveTimers.current[ruleId] = setTimeout(async () => {
        delete criterionSaveTimers.current[ruleId];
        const vPending = venueSaveTimers.current[ruleId];
        if (vPending) clearTimeout(vPending);
        delete venueSaveTimers.current[ruleId];
        const sPending = stagesSaveTimers.current[ruleId];
        if (sPending) clearTimeout(sPending);
        delete stagesSaveTimers.current[ruleId];
        setError(null);
        try {
          await api.put(`${basePath}/rules/${encodeURIComponent(ruleId)}`, {
            draft_criterion_id: criterionId,
          });
          setRules((prev) =>
            (prev ?? []).map((x) => (x.id === ruleId ? { ...x, draft_criterion_id: criterionId } : x))
          );
        } catch (e: unknown) {
          setError(e instanceof Error ? e.message : 'Failed to save draft criterion.');
          await reload();
        }
      }, 450);
    },
    [basePath, reload]
  );

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!selectedProfileId) {
    return (
      <Box sx={{ p: 3, bgcolor: '#0a0a0a', color: '#fafafa', flex: 1 }}>
        <Alert severity="info" sx={{ bgcolor: '#18181b', color: '#e4e4e7' }}>
          Select an analytic profile in the header to manage domains and rules.
        </Alert>
      </Box>
    );
  }

  const loading = domains === null || rules === null || draftCriteria === null;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0a0a0a',
        color: '#fafafa',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: 0,
          flexShrink: 0,
          borderBottom: '1px solid #27272a',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              flex: 1,
              '& .MuiTab-root': {
                color: '#a1a1aa',
                textTransform: 'none',
                '&.Mui-selected': { color: '#60a5fa' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#60a5fa' },
            }}
          >
            <Tab label="Domains" />
            <Tab label="Draft criteria" />
            <Tab label="Rules" />
            <Tab label="Dashboard" />
          </Tabs>
          {busy && <CircularProgress size={22} sx={{ color: '#667eea' }} />}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: '#27272a', color: '#fecaca' }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={28} sx={{ color: '#667eea' }} />
          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
            Loading…
          </Typography>
        </Box>
      )}

      {!loading && tab === 0 && (
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Domain"
              value={newHost}
              onChange={(e) => setNewHost(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200, input: { color: '#fafafa' }, label: { color: '#a1a1aa' } }}
            />
            <FormControlLabel
              control={
                <Switch checked={newTrusted} onChange={(_, c) => setNewTrusted(c)} sx={{ color: '#71717a' }} />
              }
              label="Trusted"
              sx={{ color: '#d4d4d8' }}
            />
            <Tooltip title="Add domain">
              <span>
                <IconButton
                  color="primary"
                  disabled={busy || !newHost.trim()}
                  onClick={() =>
                    run(async () => {
                      await api.post(`${basePath}/domains`, {
                        host_pattern: newHost.trim(),
                        trusted: newTrusted,
                        enabled: true,
                      });
                      setNewHost('');
                      setNewTrusted(false);
                    })
                  }
                  sx={{ color: '#818cf8' }}
                  size="small"
                  aria-label="Add domain"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {domains!.length > 0 && (
            <Stack spacing={1.5}>
              {domains!.map((d) => (
                <Stack
                  key={d.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ sm: 'center' }}
                  sx={{ p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#18181b' }}
                >
                  <Typography sx={{ flex: 1, fontWeight: 600, color: '#fafafa' }}>{d.host_pattern}</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={d.trusted}
                        onChange={(_, c) =>
                          run(async () => {
                            await api.put(`${basePath}/domains/${d.id}`, { trusted: c });
                          })
                        }
                        disabled={busy}
                      />
                    }
                    label="Trusted"
                    sx={{ color: '#d4d4d8' }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={d.enabled}
                        onChange={(_, c) =>
                          run(async () => {
                            await api.put(`${basePath}/domains/${d.id}`, { enabled: c });
                          })
                        }
                        disabled={busy}
                      />
                    }
                    label="Enabled"
                    sx={{ color: '#d4d4d8' }}
                  />
                  <IconButton
                    aria-label="Delete domain"
                    onClick={() =>
                      run(async () => {
                        await api.delete(`${basePath}/domains/${d.id}`);
                      })
                    }
                    disabled={busy}
                    sx={{ color: '#f87171' }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {!loading && tab === 1 && (
        <Stack spacing={2}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: { xs: '100%', sm: 780 } }}>
              <Box
                sx={{
                  ...criteriaGridSx,
                  alignItems: { sm: 'end' },
                  py: 0.75,
                  px: 1,
                  pb: 1,
                  borderBottom: '1px solid #27272a',
                  display: { xs: 'none', sm: 'grid' },
                }}
              >
                <Typography sx={rulesHeaderTypographySx}>Name</Typography>
                <Typography sx={rulesHeaderTypographySx}>Word count</Typography>
                <Typography sx={rulesHeaderTypographySx}>Output</Typography>
                <Typography
                  sx={{
                    ...rulesHeaderTypographySx,
                    justifySelf: 'center',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  Enabled
                </Typography>
                <Box sx={{ minHeight: 20 }} aria-hidden />
              </Box>

              <Box
                sx={{
                  ...criteriaGridSx,
                  py: 0.65,
                  px: 1,
                  mb: 1.25,
                  border: '1px solid #27272a',
                  borderRadius: 1,
                  bgcolor: '#141414',
                }}
              >
                <TextField
                  label="Name"
                  value={newCriterionName}
                  onChange={(e) => setNewCriterionName(e.target.value)}
                  required
                  size="small"
                  disabled={busy}
                  sx={{ width: '100%', minWidth: 0, input: { color: '#fafafa' }, label: { color: '#a1a1aa' } }}
                />
                <CriterionWordCountSlider
                  disabled={busy}
                  words={newCriterionWords}
                  onWordsChange={(w) => setNewCriterionWords(w)}
                />
                <Chip label="Article" size="small" variant="outlined" sx={draftCriterionOutputChipSx} />
                <Switch
                  checked
                  disabled
                  size="small"
                  inputProps={{ 'aria-label': 'New criteria are created enabled' }}
                  sx={{ justifySelf: 'center' }}
                />
                <Tooltip title="Add criterion">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={busy || !newCriterionName.trim()}
                      onClick={() =>
                        run(async () => {
                          const w = clampWordSlider(newCriterionWords);
                          await api.post(
                            `${basePath}/draft-criteria`,
                            criterionSavePayload(newCriterionName, w, true)
                          );
                          setNewCriterionName('');
                          setNewCriterionWords(800);
                        })
                      }
                      sx={{ color: '#818cf8', justifySelf: 'center' }}
                      size="small"
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {draftCriteria!.length > 0 && (
                <Stack spacing={1}>
                  {draftCriteria!.map((c) => (
                    <DraftCriterionRow key={c.id} c={c} busy={busy} basePath={basePath!} run={run} />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      )}

      {!loading && tab === 3 && basePath && (
        <FastTrackingDashboardTab
          basePath={basePath}
          domains={domains ?? []}
          draftCriteria={draftCriteria ?? []}
          publishVenues={publishVenues}
          rules={rules ?? []}
          onHttpError={setError}
        />
      )}

      {!loading && tab === 2 && (
        <Stack spacing={3}>
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              py: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: '#71717a' }}>
              Stages (hover / tap icons)
            </Typography>
            <RuleStagesColumnHeader />
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: { xs: '100%', sm: 940 } }}>
              <Box
                sx={{
                  ...rulesGridSx,
                  alignItems: { sm: 'end' },
                  py: 0.75,
                  px: 1,
                  pb: 1,
                  borderBottom: '1px solid #27272a',
                  display: { xs: 'none', sm: 'grid' },
                }}
              >
                <Typography sx={rulesHeaderTypographySx}>Domain</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'end', pb: 0.25 }}>
                  <RuleStagesColumnHeader />
                </Box>
                <Typography sx={rulesHeaderTypographySx}>Draft criteria</Typography>
                <Typography sx={{ ...rulesHeaderTypographySx, ...publishVenuesColumnInsetSx }}>
                  Publish venues
                </Typography>
                <Typography
                  sx={{
                    ...rulesHeaderTypographySx,
                    justifySelf: 'center',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  Enabled
                </Typography>
                <Typography
                  sx={{
                    ...rulesHeaderTypographySx,
                    justifySelf: 'center',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  Actions
                </Typography>
              </Box>

              <Box
                sx={{
                  ...rulesGridSx,
                  py: 0.75,
                  px: 1,
                  mb: 1.5,
                  border: '1px solid #27272a',
                  borderRadius: 1,
                  bgcolor: '#141414',
                }}
              >
                <Select
                  displayEmpty
                  value={newRuleDomainId}
                  onChange={(e) => setNewRuleDomainId(e.target.value as string)}
                  size="small"
                  sx={{
                    width: '100%',
                    maxWidth: { sm: 220 },
                    minWidth: 0,
                    color: '#fafafa',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' },
                  }}
                >
                  <MenuItem value="">
                    <em>Choose domain</em>
                  </MenuItem>
                  {(domains || []).map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.host_pattern}
                    </MenuItem>
                  ))}
                </Select>
                <RuleStagesCheckboxes
                  value={newRuleStages}
                  onChange={(s) => {
                    setNewRuleStages(s);
                    if (!s.publish) setNewRuleVenues([]);
                  }}
                  disabled={busy}
                />
                <DraftCriterionAutocomplete
                  criteria={draftCriteria ?? []}
                  value={newRuleCriterionId}
                  onChange={setNewRuleCriterionId}
                  disabled={busy}
                  sx={{ width: '100%', minWidth: 0 }}
                />
                <Stack spacing={0.5} sx={rulesVenuesColumnSx}>
                  <VenueAutocompleteInput
                    venues={publishVenues}
                    label="Publish venues"
                    value={newRuleVenues}
                    onChange={setNewRuleVenues}
                    disabled={busy || !newRuleStages.publish}
                    sx={{ width: '100%' }}
                  />
                  {newRuleVenues.length > 0 && (
                    <VenueChipsRow
                      venues={publishVenues}
                      value={newRuleVenues}
                      onChange={setNewRuleVenues}
                      disabled={busy || !newRuleStages.publish}
                    />
                  )}
                </Stack>
                <Switch
                  checked={newRuleEnabled}
                  onChange={(_, c) => setNewRuleEnabled(c)}
                  disabled={busy}
                  size="small"
                  inputProps={{ 'aria-label': 'Enabled' }}
                  sx={{ justifySelf: 'center' }}
                />
                <Stack direction="row" spacing={1} sx={{ gridColumn: { sm: '1 / -1' }, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={newRuleGenImage}
                        onChange={(_, c) => setNewRuleGenImage(c)}
                        disabled={busy}
                        sx={checkboxSx}
                      />
                    }
                    label="AI hero image"
                    sx={{ color: '#d4d4d8', m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={newRuleGenQuiz}
                        onChange={(_, c) => setNewRuleGenQuiz(c)}
                        disabled={busy}
                        sx={checkboxSx}
                      />
                    }
                    label="LLM quiz"
                    sx={{ color: '#d4d4d8', m: 0 }}
                  />
                </Stack>
                <Tooltip title="Add rule">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={busy || !newRuleDomainId || !newRuleCriterionId}
                      onClick={() =>
                        run(async () => {
                          let norm = { ...newRuleStages, draft: true };
                          if (!norm.draft && !norm.ready && !norm.publish) {
                            norm = stagesFromAction(ACTION_DRAFT);
                          }
                          const action = actionFromStages(norm);
                          const publishVenues = norm.publish ? newRuleVenues : [];
                          await api.post(`${basePath}/rules`, {
                            domain_id: newRuleDomainId,
                            draft_criterion_id: newRuleCriterionId,
                            action,
                            publish_venues: publishVenues,
                            enabled: newRuleEnabled,
                            generate_article_image: newRuleGenImage,
                            generate_article_quiz: newRuleGenQuiz,
                          });
                          setNewRuleDomainId('');
                          setNewRuleCriterionId('');
                          setNewRuleStages(stagesFromAction(ACTION_DRAFT));
                          setNewRuleVenues([]);
                          setNewRuleEnabled(true);
                          setNewRuleGenImage(false);
                          setNewRuleGenQuiz(false);
                        })
                      }
                      sx={{ color: '#818cf8', justifySelf: 'center' }}
                      size="small"
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Stack spacing={1}>
                  {rules!.map((r) => {
                    const dom = domains!.find((x) => x.id === r.domain_id);
                    const venues = r.publish_venues ?? [];
                    const ruleStages = stagesFromAction(r.action);
                    const rowCriterionId =
                      r.draft_criterion_id &&
                      (draftCriteria ?? []).some((c) => c.id === r.draft_criterion_id)
                        ? r.draft_criterion_id
                        : '';
                    return (
                      <Box
                        key={r.id}
                        sx={{
                          ...rulesGridSx,
                          p: 1,
                          border: '1px solid #27272a',
                          borderRadius: 1,
                          bgcolor: '#18181b',
                        }}
                      >
                        <Typography sx={{ color: '#e4e4e7', fontWeight: 600, wordBreak: 'break-word' }}>
                          {dom?.host_pattern ?? r.domain_id}
                        </Typography>
                        <RuleStagesCheckboxes
                          value={ruleStages}
                          onChange={(next) => {
                            let norm = { ...next, draft: true };
                            if (!norm.draft && !norm.ready && !norm.publish) {
                              norm = stagesFromAction(ACTION_DRAFT);
                            }
                            const action = actionFromStages(norm);
                            const nextVenues = norm.publish ? venues : [];
                            if (!norm.publish) {
                              setPublishVenuesEditingRuleId((cur) => (cur === r.id ? null : cur));
                            }
                            setRules((prev) =>
                              (prev ?? []).map((x) =>
                                x.id === r.id ? { ...x, action, publish_venues: nextVenues } : x
                              )
                            );
                            queueStagesSave(r.id, norm, nextVenues);
                          }}
                          disabled={busy}
                        />
                        <DraftCriterionAutocomplete
                          criteria={draftCriteria ?? []}
                          value={rowCriterionId}
                          onChange={(id) => {
                            setRules((prev) =>
                              (prev ?? []).map((x) => (x.id === r.id ? { ...x, draft_criterion_id: id } : x))
                            );
                            queueCriterionSave(r.id, id);
                          }}
                          disabled={busy}
                          sx={{ width: '100%', minWidth: 0 }}
                        />
                        <Stack spacing={0.5} sx={rulesVenuesColumnSx}>
                          {publishVenuesEditingRuleId === r.id ? (
                            <>
                              <VenueAutocompleteInput
                                venues={publishVenues}
                                label="Publish venues"
                                value={venues}
                                onChange={(next) => {
                                  setRules((prev) =>
                                    (prev ?? []).map((x) => (x.id === r.id ? { ...x, publish_venues: next } : x))
                                  );
                                  queueVenueSave(r.id, next);
                                }}
                                disabled={busy || !ruleStages.publish}
                                sx={{ width: '100%' }}
                              />
                              {venues.length > 0 && (
                                <VenueChipsRow
                                  venues={publishVenues}
                                  value={venues}
                                  onChange={(next) => {
                                    setRules((prev) =>
                                      (prev ?? []).map((x) => (x.id === r.id ? { ...x, publish_venues: next } : x))
                                    );
                                    queueVenueSave(r.id, next);
                                  }}
                                  disabled={busy || !ruleStages.publish}
                                />
                              )}
                            </>
                          ) : venues.length > 0 ? (
                            <VenueChipsRow
                              venues={publishVenues}
                              value={venues}
                              onChange={() => {}}
                              disabled
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: '#71717a', pl: { sm: 0.25 } }}>
                              —
                            </Typography>
                          )}
                        </Stack>
                        <Switch
                          checked={r.enabled}
                          onChange={(_, c) =>
                            run(async () => {
                              await api.put(`${basePath}/rules/${r.id}`, { enabled: c });
                            })
                          }
                          disabled={busy}
                          size="small"
                          inputProps={{ 'aria-label': 'Enabled' }}
                          sx={{ justifySelf: 'center' }}
                        />
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          sx={{ gridColumn: { sm: '1 / -1' } }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(r.generate_article_image)}
                                onChange={(_, c) => {
                                  const q = Boolean(r.generate_article_quiz);
                                  setRules((prev) =>
                                    (prev ?? []).map((x) =>
                                      x.id === r.id
                                        ? { ...x, generate_article_image: c, generate_article_quiz: q }
                                        : x
                                    )
                                  );
                                  queueEnrichmentSave(r.id, c, q);
                                }}
                                disabled={busy}
                                sx={checkboxSx}
                              />
                            }
                            label="AI hero image"
                            sx={{ color: '#d4d4d8', m: 0 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(r.generate_article_quiz)}
                                onChange={(_, c) => {
                                  const img = Boolean(r.generate_article_image);
                                  setRules((prev) =>
                                    (prev ?? []).map((x) =>
                                      x.id === r.id
                                        ? { ...x, generate_article_image: img, generate_article_quiz: c }
                                        : x
                                    )
                                  );
                                  queueEnrichmentSave(r.id, img, c);
                                }}
                                disabled={busy}
                                sx={checkboxSx}
                              />
                            }
                            label="LLM quiz"
                            sx={{ color: '#d4d4d8', m: 0 }}
                          />
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={0}
                          justifyContent="center"
                          alignItems="center"
                          sx={{ justifySelf: 'center' }}
                        >
                          <Tooltip
                            title={
                              !ruleStages.publish
                                ? 'Enable the Publish stage to edit publish venues'
                                : publishVenuesEditingRuleId === r.id
                                  ? 'Done editing publish venues'
                                  : 'Edit publish venues'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy || !ruleStages.publish}
                                onClick={() =>
                                  setPublishVenuesEditingRuleId((cur) => (cur === r.id ? null : r.id))
                                }
                                sx={{ color: '#a1a1aa' }}
                                aria-label={
                                  publishVenuesEditingRuleId === r.id
                                    ? 'Done editing publish venues'
                                    : 'Edit publish venues'
                                }
                              >
                                {publishVenuesEditingRuleId === r.id ? (
                                  <CheckIcon fontSize="small" />
                                ) : (
                                  <EditNoteOutlinedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete rule">
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() =>
                                  run(async () => {
                                    await api.delete(`${basePath}/rules/${r.id}`);
                                  })
                                }
                                sx={{ color: '#f87171' }}
                                aria-label="Delete rule"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })}
              </Stack>
            </Box>
          </Box>
        </Stack>
      )}
      </Box>
    </Box>
  );
};

export default LayoutFastTracking;
